import EngineShell, { type SubTabDef } from '../components/EngineShell';
import DataIntervention, {
  ActionIntervention, MetricTiles, MiniTable, StatusPill,
} from '../components/engine/DataIntervention';
import { api } from '../api/client';
import { useState } from 'react';
import Engine1_Formulation from './Engine1_Formulation';

/**
 * Engine 1 · Innovation (CPG value chain · stage 1).
 * Consumer insights & R&D. Rapid Formulation is the lighthouse. All sub-tabs live.
 */

interface AdjacencyResp { share_of_throat: number; brand_fit: number; revenue_pool_usd_M: number; adjacent_categories: string[] }

interface DesignOption { theme: string; big_can_format_compatible: boolean; brand_code_compliance: boolean }
interface VisualIpResp { design_options: DesignOption[] }

interface Substitute { component: string; substitute: string; savings_per_unit: number; reason: string }
interface RecipeCostResp { cost_per_unit: number; gap_to_99c: number; substitutes: Substitute[] }

interface LabelConflict { kind: string; value: string; reason: string }
interface LabelIpResp { cleared: boolean; conflicts: LabelConflict[]; hitl_level: string }

interface ConceptRow { concept_id: string; name: string; stage: string; projected_cases_k: number; cost_per_unit: number; on_brand_score: number }
interface ConceptSlateResp { slate: ConceptRow[]; scored_options: unknown[] }

const pct = (v: number) => `${v.toFixed(1)}%`;
const usd = (v: number) => (v < 0 ? `−$${Math.abs(v).toFixed(2)}` : `$${v.toFixed(2)}`);

export default function Engine1Shell() {
  const subTabs: SubTabDef[] = [
    {
      id: 'rapid_formulation', name: 'Rapid Formulation', tier: 'lighthouse', moat: 'core', enabled: true,
      render: () => <Engine1_Formulation />,
    },
    {
      id: 'adjacency_scorer', name: 'Adjacency Scorer', tier: 'flagship', moat: 'core', enabled: true,
      render: () => <AdjacencyScorer />,
    },
    {
      id: 'visual_ip_assist', name: 'Visual IP Assist', tier: 'flagship', moat: 'core', enabled: true,
      render: () => <VisualIpAssist />,
    },
    {
      id: 'recipe_to_cost', name: 'Recipe-to-Cost', tier: 'core', moat: 'core', enabled: true,
      render: () => <RecipeToCost />,
    },
    {
      id: 'label_ip_prescreen', name: 'Label/IP Pre-Screen', tier: 'flagship', moat: 'core', enabled: true,
      render: () => <LabelIpPreScreen />,
    },
    {
      id: 'concept_slate', name: 'Concept Slate', tier: 'core', moat: 'core', enabled: true,
      render: () => (
        <DataIntervention<ConceptSlateResp>
          objective="O1 · keep the launch slate full of on-brand, sub-99c concepts"
          workflow={['Pull active concept slate', 'Track stage + projected cases', 'Screen cost-per-unit + on-brand fit', 'Advance the winners']}
          decisionRules={{ rules: ['Flag any cost-per-unit ≥ $0.99', 'Hold if on-brand score < 0.80'] }}
          aiLogic={{ description: 'Deterministic slate rollup with on-brand scoring.', modality: 'deterministic', hitlLevel: 'L2 exception review', moat: 'core' }}
          kpi={{ primary: 'Slate count + projected cases + on-brand score', owner: 'CMO / R&D' }}
          queryKey={['innovation', 'concept-slate']} fetcher={() => api.get<ConceptSlateResp>('/api/innovation/concept-slate')}
          render={(d) => (
            <>
              <MetricTiles tiles={[
                { label: 'Live concepts', value: String(d.slate.length), color: 'ink' },
                { label: 'Scored options', value: String(d.scored_options.length), color: 'muted' },
              ]} />
              <MiniTable title="Concept slate" rows={d.slate} rowKey={(r) => r.concept_id}
                columns={[
                  { header: 'Concept', cell: (r) => <><b className="mono">{r.concept_id}</b> · {r.name}</> },
                  { header: 'Stage', cell: (r) => <span className="small">{r.stage}</span> },
                  { header: 'Cases', align: 'right', cell: (r) => <span className="mono">{r.projected_cases_k}k</span> },
                  { header: 'Cost/unit', align: 'right', cell: (r) => <span className="mono" style={{ color: r.cost_per_unit < 0.99 ? 'var(--olive)' : 'var(--red)' }}>{usd(r.cost_per_unit)}</span> },
                  { header: 'On-brand', align: 'right', cell: (r) => <span className="mono" style={{ color: r.on_brand_score >= 0.8 ? 'var(--olive)' : 'var(--gold)' }}>{(r.on_brand_score * 100).toFixed(0)}%</span> },
                ]} />
            </>
          )}
        />
      ),
    },
  ];

  return (
    <EngineShell
      engineId={1}
      engineName="Engine 1 · Innovation (Consumer Insights & R&D)"
      engineCode="E1"
      subTabs={subTabs}
      backgrounds={[{ id: 'trend-claims', name: 'Trend/Claims Watcher', status: 'ok' }]}
    />
  );
}

/* Adjacency Scorer — POST with a category input */
function AdjacencyScorer() {
  const [category, setCategory] = useState('functional tea');
  return (
    <ActionIntervention<AdjacencyResp>
      objective="O1 · size adjacent categories the brand can credibly win"
      workflow={['Enter a candidate category', 'Score share-of-throat + brand fit', 'Size the revenue pool', 'Shortlist adjacent plays']}
      decisionRules={{ rules: ['Pursue if brand fit ≥ 0.80', 'Prioritize pools > $1B'] }}
      aiLogic={{ description: 'Deterministic adjacency scoring from category and brand-fit signals.', modality: 'deterministic', hitlLevel: 'L2 exception review', moat: 'core' }}
      kpi={{ primary: 'Share-of-throat + brand fit + revenue pool ($M)', owner: 'CMO / Strategy' }}
      initialState={{ category, brand_fit: 0.84, revenue_pool_usd_M: 1200, adjacent_categories: [category] }}
      input={
        <>
          <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Category</div>
          <input className="c2-input" value={category} onChange={(e) => setCategory(e.target.value)} style={{ width: 240 }} />
        </>
      }
      runLabel="Score adjacency"
      action={() => api.innovationAdjacency(category) as Promise<AdjacencyResp>}
      render={(d) => (
        <>
          <MetricTiles tiles={[
            { label: 'Share of throat', value: pct(d.share_of_throat * 100), color: 'ink' },
            { label: 'Brand fit', value: pct(d.brand_fit * 100), color: d.brand_fit >= 0.8 ? 'olive' : 'gold' },
            { label: 'Revenue pool', value: `$${d.revenue_pool_usd_M.toFixed(0)}M`, color: 'olive' },
          ]} />
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {d.adjacent_categories.map((c) => <span key={c} className="tag">{c}</span>)}
          </div>
        </>
      )}
    />
  );
}

/* Visual IP Assist — POST with a creative brief */
function VisualIpAssist() {
  const [brief, setBrief] = useState('summer botanical');
  return (
    <ActionIntervention<VisualIpResp>
      objective="O1 · generate big-can-ready, brand-compliant design directions"
      workflow={['Enter a creative brief', 'Generate design options', 'Check big-can format + brand code', 'Route cleared options to packaging']}
      decisionRules={{ rules: ['Ship only big-can-compatible options', 'Block any brand-code failure'] }}
      aiLogic={{ description: 'Generative design directions screened against the brand code book.', modality: 'genai', hitlLevel: 'L3 approval gating', moat: 'core' }}
      kpi={{ primary: 'Cleared design options (format + brand code)', owner: 'Creative / Brand' }}
      initialState={{
        design_options: [
          { theme: brief, big_can_format_compatible: true, brand_code_compliance: true },
          { theme: 'botanical line art', big_can_format_compatible: true, brand_code_compliance: true },
        ],
      }}
      input={
        <>
          <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Brief</div>
          <input className="c2-input" value={brief} onChange={(e) => setBrief(e.target.value)} style={{ width: 240 }} />
        </>
      }
      runLabel="Generate directions"
      action={() => api.innovationVisualIp(brief) as Promise<VisualIpResp>}
      render={(d) => (
        <MiniTable title="Design options" rows={d.design_options} rowKey={(r, i) => `${r.theme}-${i}`}
          columns={[
            { header: 'Theme', cell: (r) => <b>{r.theme}</b> },
            { header: 'Big-can', align: 'right', cell: (r) => <StatusPill status={r.big_can_format_compatible ? 'pass' : 'fail'} /> },
            { header: 'Brand code', align: 'right', cell: (r) => <StatusPill status={r.brand_code_compliance ? 'pass' : 'fail'} /> },
          ]} />
      )}
    />
  );
}

/* Recipe-to-Cost — POST with a fixed formula object */
function RecipeToCost() {
  return (
    <ActionIntervention<RecipeCostResp>
      objective="O1 · land every concept under the 99c hero-can ceiling"
      workflow={['Cost the bill of materials', 'Compute gap to 99c', 'Surface cheaper substitutes', 'Re-cost with swaps']}
      decisionRules={{ rules: ['Block launch if gap to 99c is negative', 'Prefer substitutes with no brand-code impact'] }}
      aiLogic={{ description: 'Deterministic BOM costing with substitute search.', modality: 'deterministic', hitlLevel: 'L2 exception review', moat: 'core' }}
      kpi={{ primary: 'Cost-per-unit + gap to 99c + substitutes', owner: 'R&D / Cost engineering' }}
      initialState={{
        cost_per_unit: 0.54,
        gap_to_99c: 0.45,
        target_cost_usd: 0.55,
        substitutes: [{ component: 'functional', substitute: 'l-theanine blend', savings_per_unit: 0.04 }],
      }}
      input={
        <div className="small faint">Formula: green tea · cane sugar · l-theanine · 23oz can</div>
      }
      runLabel="Cost recipe"
      action={() => api.innovationRecipeToCost({ base: 'green tea', sweetener: 'cane sugar', functional: 'l-theanine', pack: '23oz can' }) as Promise<RecipeCostResp>}
      render={(d) => (
        <>
          <MetricTiles tiles={[
            { label: 'Cost / unit', value: usd(d.cost_per_unit), color: d.cost_per_unit < 0.99 ? 'olive' : 'red' },
            { label: 'Gap to 99c', value: usd(d.gap_to_99c), color: d.gap_to_99c < 0 ? 'red' : 'olive' },
          ]} />
          <MiniTable title="Substitutes" rows={d.substitutes} rowKey={(r, i) => `${r.component}-${i}`}
            columns={[
              { header: 'Component', cell: (r) => <b>{r.component}</b> },
              { header: 'Substitute', cell: (r) => <span className="small">{r.substitute}</span> },
              { header: 'Savings/unit', align: 'right', cell: (r) => <span className="mono" style={{ color: 'var(--olive)' }}>{usd(r.savings_per_unit)}</span> },
              { header: 'Reason', cell: (r) => <span className="small faint">{r.reason}</span> },
            ]} />
        </>
      )}
    />
  );
}

/* Label/IP Pre-Screen — POST with fixed claims + marks */
function LabelIpPreScreen() {
  return (
    <ActionIntervention<LabelIpResp>
      objective="O1 · clear claims and marks before art locks"
      workflow={['Submit claims + marks', 'Screen against claim + trademark rules', 'Surface conflicts', 'Route to legal at the right HITL level']}
      decisionRules={{ rules: ['Block any unsubstantiated health claim', 'Escalate mark conflicts to legal'] }}
      aiLogic={{ description: 'RAG over claim/IP corpus with deterministic conflict rules.', modality: 'rag', hitlLevel: 'L3 approval gating', moat: 'core' }}
      kpi={{ primary: 'Cleared status + open conflicts + HITL level', owner: 'Legal / Regulatory' }}
      initialState={{
        cleared: false,
        hitl_level: 'L3 approval gating',
        conflicts: [{ kind: 'mark', value: 'Pure Leaf Energy', reason: 'competitor mark conflict' }],
      }}
      input={
        <div className="small faint">Claims: "clinically proven focus" · Marks: "Pure Leaf Energy"</div>
      }
      runLabel="Pre-screen label"
      action={() => api.post<LabelIpResp>('/api/innovation/label-ip/pre-screen', { claims: ['clinically proven focus'], marks: ['Pure Leaf Energy'] })}
      render={(d) => (
        <>
          <MetricTiles tiles={[
            { label: 'Cleared', value: d.cleared ? 'Yes' : 'No', color: d.cleared ? 'olive' : 'red' },
            { label: 'Conflicts', value: String(d.conflicts.length), color: d.conflicts.length ? 'red' : 'olive' },
            { label: 'HITL level', value: d.hitl_level, color: 'ink' },
          ]} />
          <MiniTable title="Conflicts" rows={d.conflicts} rowKey={(r, i) => `${r.kind}-${i}`}
            columns={[
              { header: 'Kind', cell: (r) => <b className="small">{r.kind}</b> },
              { header: 'Value', cell: (r) => <span className="mono small">{r.value}</span> },
              { header: 'Reason', cell: (r) => <span className="small faint">{r.reason}</span> },
            ]} />
        </>
      )}
    />
  );
}
